import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { Alert, App, Button, Select, Switch, Tag, Tooltip } from 'antd';

import ItemCardsGrid from '../components/items/ItemCardsGrid';
import CardFilterSelect, { CARD_NAME_ROW_CHARS } from '../components/items/CardFilterSelect';
import useTileLayouts from '../hooks/useTileLayouts';
import useTableLayouts from '../hooks/useTableLayouts';
import useWindowClass from '../hooks/useWindowClass';
import TileLayoutModal from '../components/cards/TileLayoutModal';
import TableColumnsModal from '../components/items/TableColumnsModal';
import { defaultTableColumns, resolveTableColumns } from '../components/items/cardTableModel';
import { withSavedColumns } from '../utils/savedFilterView';
import ItemDetailPane, { ItemPaneGone } from '../components/items/ItemDetailPane';
import RefreshButton from '../components/common/RefreshButton';
import GroupHeader from '../components/common/GroupHeader';
import ItemFilterBuilder, { ITEM_FILTER_TRIGGER_ID } from '../components/items/ItemFilterBuilder';
import FilterDriftWarning from '../components/items/FilterDriftWarning';
import QueryRefusalPick from '../components/items/QueryRefusalPick';
import ItemFilterChips from '../components/items/ItemFilterChips';
import ItemsTable from '../components/items/ItemsTable';
import ItemsBulkSurfaces from '../components/items/ItemsBulkSurfaces';
import useItemsBulkState from '../hooks/useItemsBulkState';
import useItemsViewParams from '../hooks/useItemsViewParams';
import useWorklistMode, { useWorklistFeed } from '../hooks/useWorklistMode';
import QuickFilterChips from '../components/common/QuickFilterChips';
import useSavedFilterControls from '../hooks/useSavedFilterControls';
import { FilterByValueProvider } from '../components/common/FilterByValueContext';
import { recordView } from '../services/views';
import {
	CardOptionLabel,
	buildCardHierarchyList,
	cardOwnerOf,
	cardSearchText,
	groupFavoriteCardOptions,
	resolveFavoriteKind,
} from '../components/cards/CardVisuals';
import { optionSearchFilter } from '../components/Input';
import { CLOSE, COLUMNS, GRID, KANBAN, MAP, PLUS } from '../components/icons';
import { api } from '../api/client';
import { API_ITEMS_URL } from '../utils';
import { useCollectionContext } from '../contexts/CollectionContext';
import { useCurrentUser } from '../contexts/CurrentUserContext';
import { useCardDefinitions } from '../contexts/CardDefinitionsContext';
import { useOptionalFavorites } from '../contexts/FavoritesContext';
import { RATE_LIMITED_TEXT } from '../utils/feedMessages';
import useListFilter from '../hooks/useListFilter';
import useListSort, { BASE_SORT_OPTIONS, useListSortOrder } from '../hooks/useListSort';
import useViewMode from '../hooks/useViewMode';
import SortLevels from '../components/items/SortLevels';
import ViewModeSwitch from '../components/items/ViewModeSwitch';
import ListCount from '../components/items/ListCount';
import ListSearchBox from '../components/items/ListSearchBox';
import NoItemsMatch from '../components/items/NoItemsMatch';
import useListSearch from '../hooks/useListSearch';
import useCollectionCounts from '../hooks/useCollectionCounts';
import { listSearchAvailable } from '../config/listSearch';
import ListScrollLoader from '../components/items/ListScrollLoader';
import CollectionItemsToolbar from '../components/items/CollectionItemsToolbar';
import useSettledValue, { SELECTION_SETTLE_MS } from '../hooks/useSettledValue';
import useLinkedItems from '../hooks/useLinkedItems';
import { normalizeId } from '../utils/ids';
import {
	getCollectionFacetCardKeys,
	getCollectionFavoriteCardIds,
	getCollectionHiddenQuickFilters,
	getCollectionFamilyCardIds,
	getCollectionQuickFilterPins,
	collectionHasNoCards,
} from '../utils/collections';
import ItemsListBody from '../components/items/ItemsListBody';
import NoCardsEmptyState from '../components/collections/NoCardsEmptyState';
import NoItemsEmptyState from '../components/collections/NoItemsEmptyState';
import { buildFilterFields, parseForBuilder, readEqualsTerm, tagsFilterField, withEqualsTerm } from '../utils/kqlBuilder';
import useCollectionTags from '../hooks/useCollectionTags';
import { useOptionalTags } from '../contexts/TagsContext';
import { itemTagTitlesOf } from '../utils/tagTitleMemory';
import { serialize } from '../utils/kql';
import { ancestorsOf, cardHomeOf, subCollectionsOf } from '../utils/collectionFamily';
import RecordBadge from '../components/common/RecordBadge';
import { cardIdsInScope, createCardResolver } from '../utils/filterScope';
import { createFacetResolver, quickFilterFields } from '../utils/facets';
import useApplyValueFilter from '../hooks/useApplyValueFilter';
import { replaceRecentFilter } from '../utils/recentFilters';
import { canWriteCard } from '../utils/permissions';
import { createLookupResolver } from '../utils/lookups';
import ItemWorklist from '../components/items/ItemWorklist';
import { localItemsViewStore } from '../utils/itemsView';
import { REVIEW_LABELS } from '../utils/itemsReview';
import {
	UNDATED_KEY,
	buildItemGroups,
	groupDisabledReasonFor,
	groupOptionsFor,
	isGroupToken,
	resolveGroupToken,
	subDayAllowed,
} from '../utils/itemGroups';
import {
	readStoredGroupCollapse,
	readStoredItemsGroup,
	writeStoredGroupCollapse,
	writeStoredItemsGroup,
} from '../utils/itemsGroup';
import useCollectionAccess from '../hooks/useCollectionAccess';
import useLinkedSources from '../hooks/useLinkedSources';
import useLinkedCount from '../hooks/useLinkedCount';
import { canSeeLinks } from '../utils/collectionLinks';
import {
	borrowedFromOf,
	isReadOnlyRow,
	itemHomeCollectionId,
	resolveLinkedParam,
	sourceQualifierOf,
} from '../utils/linkedRecords';
import GlobalLiveNotice from '../components/collections/GlobalLiveNotice';
import useCombinedScope from '../hooks/useCombinedScope';
import useRowDensity from '../hooks/useRowDensity';
import usePaneWidth from '../hooks/usePaneWidth';
import useListStep from '../hooks/useListStep';
import useItemsListKeys from '../hooks/useItemsListKeys';
import { GlobalDisclaimer } from '../components/globalCollections/ListingDetails';
import ItemsMapView from '../components/items/ItemsMapView';
import useMapItems from '../hooks/useMapItems';
import useRankMove from '../hooks/useRankMove';
import { rankSortOf } from '../utils/rank';
import ItemsBoard from '../components/items/ItemsBoard';
import BoardControls from '../components/items/BoardControls';
import {
	boardElements,
	boardOrder,
	layoutBoard,
	readStoredBoard,
	resolveBoard,
	storedBoardOf,
	writeStoredBoard,
} from '../utils/board';
import { mapViewConfigured } from '../config/mapTiles';
import { anyCardHasLocation, placeItems } from '../utils/itemLocations';

/** One shared empty set, so "nothing flipped" never re-renders the feed. */
const NO_FLIPPED_GROUPS = new Set();
// A stable "no private tags" (outside a TagsProvider, or signed out).
const NO_TAGS = Object.freeze([]);

/**
 * How many quick-filter chips the toolbar shows before the rest go behind
 * "More filters". A taste call made against a real seventeen-card collection:
 * six is the most a desktop toolbar carries without the row becoming a
 * paragraph, and three is what a phone has room for beside the view switch.
 * An ACTIVE chip is never in the overflow, whatever the cap says.
 */
const QUICK_FILTER_LIMIT_DESKTOP = 6;
const QUICK_FILTER_LIMIT_MOBILE = 3;

// The toolbar's search box is the query's `text` condition (KPR-740); its chip
// is hidden while the box is there to show it.
const SEARCH_PATHS = Object.freeze(['text']);
const NO_PATHS = Object.freeze([]);
const ITEMS_SEARCH_ID = 'items-search';
// How long an item opened by address waits for its tile to be drawn before the
// page stops looking to scroll to it (KPR-1244).
const TILE_WAIT_MS = 8000;

export default function CollectionItemsPage() {
	const { collectionId } = useParams();
	const collectionContext = useCollectionContext();
	const collection = collectionContext?.collection;
	const collectionLoading = collectionContext?.collectionLoading;
	const refreshCollection = collectionContext?.refreshCollection;

	const {
		cards,
		cardsById,
		getElementsForCard,
		cardPrimaryDate,
		// The readable element sets: the `set` filter field's choices.
		elementSets,
		loading: cardsLoading,
		error: cardsError,
		// Bumps when the cache refetches (a card added after the page loaded):
		// the tile layouts refetch beside it.
		version: cardsVersion,
	} = useCardDefinitions();

	const { message } = App.useApp();
	const {
		cardFilter,
		query,
		review,
		sortField: urlSortField,
		sortDirection: urlSortDirection,
		sortThen: urlSortThen,
		sortChosen,
		selectedItemId,
		groupParam,
		linkedParam,
		savedColumns,
		setSavedColumns,
		savedFilterId,
		update,
		setQuery,
		setSelectedItemId,
	} = useItemsViewParams();

	// ---- The merged list (keepr-api § Merged lists) ----
	// A collection that reads from others through collection links shows
	// their records in its own list, read-only, each saying where it lives.
	// ON by default wherever there is something to merge; `?linked=0` turns
	// it off and stays in the address (per collection, shareable).
	const linked = useLinkedSources(collectionId, { enabled: canSeeLinks(collection) });
	// This route's collection document is not in hand yet (the layout is
	// fetching it, or is about to: its effect runs after this page's), so
	// whether links can be read here is not known either. A refresh keeps
	// the document it had, and a failed fetch is an answer — neither waits.
	const collectionPending = Boolean(collectionContext)
		&& !collectionContext.collectionError
		&& normalizeId(collection?._id) !== normalizeId(collectionId)
		&& (Boolean(collectionLoading) || normalizeId(collectionContext.resolvedCollectionId) === normalizeId(collectionId));
	const linkedOn = resolveLinkedParam(linkedParam, linked.hasIncoming);
	const borrowedCards = linked.borrowedCards;
	const borrowedCardIds = useMemo(
		() => new Set(borrowedCards.map(card => normalizeId(card?._id)).filter(Boolean)),
		[borrowedCards]
	);
	const setLinkedOn = useCallback(on => update({ linked: on ? undefined : '0' }), [update]);

	// Grid/table: the stored per-collection preference seeds the page, but the
	// ACTIVE mode also rides in the URL (?view=) once the user touches the
	// switch — surfaces outside this page (the header's "Export view…") read
	// what the user is looking at from there, like the card filter and sort.
	// An unrecognised ?view= falls back to the stored preference.
	const [viewSearchParams, setViewSearchParams] = useSearchParams();
	const viewStore = useMemo(() => localItemsViewStore(collectionId), [collectionId]);
	const { mode: viewMode, setMode: changeViewMode } = useViewMode({
		store: viewStore,
		url: {
			value: viewSearchParams.get('view'),
			write: (mode) => {
				setViewSearchParams(prev => {
					const next = new URLSearchParams(prev);
					next.set('view', mode);
					return next;
				}, { replace: true });
			},
		},
	});

	// Which columns each card's TABLE shows, cascaded user -> collection ->
	// card by the API (kind 'table'). Fetched only while the table is on
	// screen; a card nobody arranged is absent and shows every field.
	const { layouts: tableLayouts, refresh: refreshTableLayouts } = useTableLayouts(
		collectionId, cardsVersion, { enabled: viewMode === 'table' }
	);
	// An applied saved filter's own columns (`?cols=`, KPR-1641) stand in for its card's layout.
	const tableLayoutsShown = useMemo(
		() => withSavedColumns(tableLayouts, normalizeId(cardFilter), savedColumns, cardFilter ? getElementsForCard(cardFilter) : []),
		[savedColumns, cardFilter, tableLayouts, getElementsForCard]
	);
	// A table layout may carry a default ORDER for its card. It applies only
	// where the URL names that one card (so the feed's sort is known before
	// the feed is fetched) and the reader has not chosen an order themselves;
	// a field the card no longer has is ignored rather than sent.
	const sortOrder = useListSortOrder({
		field: urlSortField,
		direction: urlSortDirection,
		then: urlSortThen,
		chosen: sortChosen,
		tableOn: viewMode === 'table',
		cardFilter,
		tableLayouts,
		getElementsForCard,
	});
	const { sortField, sortDirection } = sortOrder;

	// The grouping period rides the same rails: stored per collection, and in
	// the URL once touched. `day` is the floor and never written.
	const [storedGroup, setStoredGroup] = useState(() => readStoredItemsGroup(collectionId));
	useEffect(() => {
		setStoredGroup(readStoredItemsGroup(collectionId));
	}, [collectionId]);
	const groupToken = isGroupToken(groupParam) ? groupParam : storedGroup;
	const setGroupToken = useCallback((token) => {
		setStoredGroup(token);
		writeStoredItemsGroup(collectionId, token);
		update({ group: token });
	}, [collectionId, update]);

	// ---- Worklist mode, the feed's half (hooks/useWorklistMode): the card
	//      LATCHED on entering the mode, and the query the feed runs — the
	//      worklist's filter narrows the list SERVER-side, so it is composed
	//      here, before the feed is fetched ----
	const worklistFeed = useWorklistFeed({ viewMode, cardsById, query });
	const { feedQuery } = worklistFeed;

	// ---- The search box (KPR-740): the query's top-level `text ~ "term"`,
	//      kept in the address with the rest of the query ----
	const search = useListSearch({ query, setQuery });
	// The gate (owner ruling t05, config/listSearch.js): over about 15,000
	// items the box waits for KPR-748's live timing. The size is the family's
	// item count, asked once per collection; until it is known the box shows.
	const countsInput = useMemo(() => (collection ? [collection] : []), [collection]);
	const collectionCounts = useCollectionCounts(countsInput)[normalizeId(collection?._id)];
	const listSize = collectionCounts ? (collectionCounts.familyItems ?? collectionCounts.items) : null;
	const searchAvailable = listSearchAvailable(listSize);

	// The filter and the feed it asks (hooks/useListFilter): refusals the person
	// settles by picking, a composed worklist feed's refusal (which never clears
	// their own filter), the canonical text the server read the filter as, and
	// the ids it says are gone. The feed itself is useCollectionItems, with the
	// merged list ON by default wherever links bring records in, so the first
	// request waits until the collection (whose myAccess says whether its links
	// can be read) and then its links are known — not one plain fetch and then
	// the merged one. A reader the links are not for (canSeeLinks) and a
	// `?linked=0` view skip the second wait.
	const feedEnabled = !collectionPending && !(canSeeLinks(collection) && linked.loading && resolveLinkedParam(linkedParam, true));
	const {
		items,
		totalCount,
		totalKey,
		loading: itemsLoading,
		loadingMore,
		hasMore,
		loadError: itemsLoadError,
		landedPages,
		queryWarnings,
		queryWarningsOmitted,
		loadMore,
		retry: retryItems,
		refresh: refreshItems,
		reload: reloadItems,
		reloading: itemsReloading,
		reloadError: itemsReloadError,
		removeItem,
		replaceItem,
		elementChoices,
		queryRefusal,
		pickForRefusal,
		repick,
		composedError: worklistFeedError,
		adoptions,
		removedIds,
	} = useListFilter({
		query,
		setQuery,
		feedQuery,
		composedMessages: {
			error: 'The server would not run this card’s worklist filter.',
			toast: 'This card’s worklist filter could not be read.',
		},
		// So a view saved from here names exactly what the list shows, the
		// recent filters keep the canonical spelling too.
		onAdopted: (typed, canonical) => replaceRecentFilter(collectionId, typed, canonical),
		// A term the server refuses: the list keeps its other filters.
		onSearchRefused: search.onRefused,
		collectionId,
		cardFilter,
		includeLinked: linkedOn,
		review,
		...sortOrder,
		enabled: feedEnabled,
	});

	// How many linked records the "Include linked" switch adds, under this
	// list's own filters: the list's total is one side, the other is asked
	// once per question (useLinkedCount).
	const linkedCount = useLinkedCount({
		collectionId,
		cardFilter,
		query: feedQuery,
		elementChoices,
		review,
		enabled: linked.hasIncoming,
		includeLinked: linkedOn,
		total: totalCount,
		totalKey,
	});

	const itemsById = useLinkedItems({
		items,
		getElementsForCard,
		enabled: !cardsLoading,
		resetKey: collectionId,
	});

	const { isCardScoped, accessibleCardIds, canManage, canCreateItems } = useCollectionAccess();
	// The worklist stamps who filed the pass when the card asks for it.
	const { user: currentUser } = useCurrentUser();

	// "Filter by this value", from wherever a value is shown (a field row, a
	// table cell, a lookup title, the row's ⋯). One door for all of them, so
	// the AND / dedupe / replace semantics are decided once — in
	// utils/filterValues.js, not per surface.
	const applyValueFilter = useApplyValueFilter({ query, setQuery });

	// null = unrestricted; a Set restricts (and MAY be empty — an empty
	// restriction must show no options, never fall open to "all cards").
	const collectionCardIds = useMemo(() => {
		// Two-source membership: embedded refs plus cards OWNED by this
		// collection (members with no config entry yet) — and, in a family,
		// what the list can hold: a sub-collection's inherited cards, a
		// parent's sub-collections' own (keepr-api § Sub-collections).
		const memberIds = getCollectionFamilyCardIds(collection, cards);
		// A card-scoped user's filter only offers the cards their grants cover
		// (the server scopes the items themselves; this keeps the picker honest).
		if (isCardScoped) {
			const allowed = new Set(accessibleCardIds);
			if (memberIds.size === 0) return allowed;
			return new Set([...memberIds].filter(id => allowed.has(id)));
		}
		return memberIds.size ? memberIds : null;
	}, [collection, cards, isCardScoped, accessibleCardIds]);

	// The cards this LIST can hold: the collection's own, plus — while the
	// merged list is on — the cards linked collections lend it. The filter
	// vocabulary and the card filter read this; a borrowed card's element is
	// as askable as one of this collection's (the API compiles `?q=` against
	// both, this collection's definition of a shared name first).
	const listCardIds = useMemo(() => {
		if (!linkedOn || !borrowedCardIds.size || !collectionCardIds) return collectionCardIds;
		return new Set([...collectionCardIds, ...borrowedCardIds]);
	}, [linkedOn, borrowedCardIds, collectionCardIds]);

	// The combined list of the ONE card this List shows (KPR-1022): the scope
	// switch "This collection · All my <Card plural>" and the one-time
	// suggestion, where two or more of the person's collections hold it.
	const scopeCardId = cardFilter || (collectionCardIds && collectionCardIds.size === 1 ? [...collectionCardIds][0] : null);
	const scopeCard = scopeCardId ? cardsById[normalizeId(scopeCardId)] || null : null;
	const scopeElements = useMemo(() => (scopeCardId ? getElementsForCard(scopeCardId) || [] : []), [scopeCardId, getElementsForCard]);
	const combinedScope = useCombinedScope({ card: scopeCard, elements: scopeElements, collectionId, query, view: viewMode });

	// Which cards the collection NAMES as facets; absent means automatic (every
	// target card two or more differently named lookups point at).
	const facetCardKeys = useMemo(() => getCollectionFacetCardKeys(collection), [collection]);

	// WHICH CARDS THIS LIST CAN ACTUALLY RETURN, read off the active filter —
	// the `?card=` param and the `card` clauses of `q`, nothing else. (`set =`
	// is a real card-level narrowing too, and is deliberately unhandled: it
	// yields "unrestricted", which is the safe direction.)
	// It is what narrows the chip row from "every question the collection can
	// ask" to "the questions this list can answer".
	//
	// NEVER from `uniqueCardInFiltered` below: that reads the LOADED items, so
	// the chip row would re-order and re-populate as each page of the infinite
	// feed arrived. Scope has to be a pure function of the URL.
	// A card KEY in the query names a card in the API's fixed order — own,
	// ancestors', sub-collections', members, globals, then what a link lends
	// (deterministic filters, ruling E1).
	const keyTiers = useMemo(() => ({
		collectionId,
		ancestorIds: ancestorsOf(collection).map(entry => entry?._id),
		descendantIds: subCollectionsOf(collection).map(entry => entry?._id),
		borrowedIds: borrowedCardIds,
	}), [collectionId, collection, borrowedCardIds]);
	const { ids: scopeCardIds } = useMemo(
		() => cardIdsInScope({ cards, memberCardIds: listCardIds, cardFilter, query, ...keyTiers }),
		[cards, listCardIds, cardFilter, query, keyTiers]
	);

	// ---- The map (KPR-392) ----
	// Offered where the build has map tiles (config/mapTiles.js) and a card
	// this list can return — the same scope the chip row reads — has a
	// location element. While the cards are still loading a `?view=map` keeps
	// the map's own loading state rather than flashing the grid. Anywhere
	// else the mode reads as the grid, its segment absent: a link or a
	// remembered choice that cannot apply here degrades, it never breaks.
	const mapCardIds = useMemo(
		() => scopeCardIds || listCardIds || new Set((cards || []).map(card => normalizeId(card?._id)).filter(Boolean)),
		[scopeCardIds, listCardIds, cards]
	);
	const listHasLocation = useMemo(
		() => anyCardHasLocation(mapCardIds, getElementsForCard),
		[mapCardIds, getElementsForCard]
	);
	const mapOffered = mapViewConfigured() && (listHasLocation || (viewMode === 'map' && (cardsLoading || collectionPending)));
	const mapOn = viewMode === 'map' && mapOffered;

	// ---- The board (KPR-1395) ----
	// Offered where every card this list can return — the map's scope —
	// shares an element a board can be drawn by (utils/board.js). While the
	// cards are still loading a `?view=board` keeps the board's own waiting
	// state; anywhere else the mode reads as the grid, its segment absent.
	const boardOptions = useMemo(
		() => boardElements(mapCardIds, getElementsForCard),
		[mapCardIds, getElementsForCard]
	);
	const boardOffered = boardOptions.length > 0 || (viewMode === 'board' && (cardsLoading || collectionPending));
	const boardOn = viewMode === 'board' && boardOffered;
	// Which element, and which swimlanes: the address's (a link, a saved
	// view), else what this person last drew this collection by, else the
	// first element the list can be drawn by.
	const [storedBoard, setStoredBoard] = useState(() => readStoredBoard(collectionId));
	useEffect(() => { setStoredBoard(readStoredBoard(collectionId)); }, [collectionId]);
	const boardParam = viewSearchParams.get('board');
	const lanesParam = viewSearchParams.get('lanes');
	const boardAsked = useMemo(
		() => (boardParam ? { by: boardParam, lanes: lanesParam || null } : storedBoard),
		[boardParam, lanesParam, storedBoard]
	);
	const board = useMemo(() => resolveBoard(boardOptions, boardAsked), [boardOptions, boardAsked]);
	const changeBoard = useCallback(({ by, lanes }) => {
		const next = { by: by || null, lanes: lanes || null };
		setStoredBoard(next);
		writeStoredBoard(collectionId, next);
		update({ board: next.by || undefined, lanes: next.lanes || undefined });
	}, [collectionId, update]);

	// How the save dialog says it: "a board by Status, with swimlanes by Owner".
	const boardText = board
		? `a board by ${board.by.label}${board.lanes ? `, with swimlanes by ${board.lanes.label}` : ''}`
		: '';

	// What is on screen: the mode, less a map or a board that cannot be drawn here.
	const effectiveView = (viewMode === 'map' && !mapOffered) || (viewMode === 'board' && !boardOffered) ? 'grid' : viewMode;

	// The map asks the list's own question (hooks/useMapItems), and only
	// while it is on screen; until the list knows what it means (the
	// collection and its links still loading) it waits, saying so.
	const mapItems = useMapItems({
		collectionId,
		cardFilter,
		query: feedQuery,
		elementChoices,
		review,
		includeLinked: linkedOn,
		...sortOrder,
		enabled: mapOn,
		hold: !feedEnabled,
	});
	const mapResolveElements = useCallback(
		cardId => (typeof getElementsForCard === 'function' ? getElementsForCard(cardId) || [] : []),
		[getElementsForCard]
	);
	const mapPlacement = useMemo(
		() => (mapOn ? placeItems(mapItems.items, mapResolveElements) : { placed: [], unplaced: [] }),
		[mapOn, mapItems.items, mapResolveElements]
	);
	// The map's list order, which ↑↓ walk and the phone's inline detail
	// grows inside: the placed items, then the ones with no location.
	const mapOrder = useMemo(
		() => [...mapPlacement.placed.map(point => point.item), ...mapPlacement.unplaced],
		[mapPlacement]
	);

	// The board reads the list's own question whole, as the map does: pages
	// of 200, the first thousand, Show more to five thousand.
	const boardItems = useMapItems({
		collectionId,
		cardFilter,
		query: feedQuery,
		elementChoices,
		review,
		includeLinked: linkedOn,
		...sortOrder,
		enabled: boardOn,
		hold: !feedEnabled,
	});
	// The list's lookups read as titles (the board's columns, the pane, the
	// tiles): defined here so the board's walk can order by them.
	const resolveElements = useCallback(
		cardId => (typeof getElementsForCard === 'function' ? getElementsForCard(cardId) || [] : []),
		[getElementsForCard]
	);
	const resolveLookupValue = useMemo(() => createLookupResolver({
		itemsById,
		cardsById,
		getElementsForCard: resolveElements,
	}), [itemsById, cardsById, resolveElements]);

	// The order ↑↓ walk on the board: column by column, lane by lane.
	const boardWalk = useMemo(
		() => (boardOn && board ? boardOrder(layoutBoard({
			items: boardItems.items,
			by: board.by,
			lanes: board.lanes,
			// The board's own order: a linked record's column by its title.
			labelFor: (element, key) => (element?.dataType === 'card-lookup' && typeof resolveLookupValue === 'function' ? resolveLookupValue(key) || '' : ''),
		})) : []),
		[boardOn, board, boardItems.items, resolveLookupValue]
	);

	// The collection's author control over the chip row (presentation only —
	// a hidden chip's condition still parses, still compiles, still filters).
	const pinnedQuickFilters = useMemo(() => getCollectionQuickFilterPins(collection), [collection]);
	const hiddenQuickFilters = useMemo(() => getCollectionHiddenQuickFilters(collection), [collection]);

	// A lookup into a card a linked collection lends (a hop, keepr-api PR L5)
	// offers that card's fields too, each saying which collection it reads.
	const hopTargets = useMemo(() => {
		const map = new Map();
		borrowedCards.forEach(card => {
			const from = borrowedFromOf(card);
			const id = normalizeId(card?._id);
			// A lender the API did not name has no name here (never "another collection").
			if (from && id) map.set(id, { collectionId: from.collectionId, name: from.named ? from.name : null });
		});
		return map;
	}, [borrowedCards]);

	// The filter vocabulary is the union of elements across the same cards the
	// picker offers — so a card-scoped user can never filter (or infer) on an
	// element of a card they cannot read — led by the collection's facets.
	// A parent's family — itself, then each sub-collection the caller reads —
	// for the `collection` field and the Sub-collection select. Empty for a
	// collection with none.
	const subCollections = useMemo(() => subCollectionsOf(collection), [collection]);
	const familyMembers = useMemo(() => (
		subCollections.length && collection ? [{ _id: normalizeId(collection._id), name: collection.name }, ...subCollections] : []
	), [collection, subCollections]);

	// A parent's table can name each row's collection in a column of its own
	// (off by default; per viewer, per collection — a convenience, so
	// browser storage, and the page works without it).
	const collectionColumnKey = collectionId ? `keepr.items.collectionColumn:${collectionId}` : null;
	const [showCollectionColumn, setShowCollectionColumn] = useState(false);
	useEffect(() => {
		let stored = false;
		try { stored = collectionColumnKey ? window.localStorage.getItem(collectionColumnKey) === '1' : false; } catch { stored = false; }
		setShowCollectionColumn(stored);
	}, [collectionColumnKey]);
	const toggleCollectionColumn = useCallback(() => {
		setShowCollectionColumn(current => {
			const next = !current;
			try {
				if (collectionColumnKey) {
					if (next) window.localStorage.setItem(collectionColumnKey, '1');
					else window.localStorage.removeItem(collectionColumnKey);
				}
			} catch { /* a convenience only */ }
			return next;
		});
	}, [collectionColumnKey]);
	// TAGS (docs/plans/tags.md §5.2): the `tags` field every filter surface
	// shares — the builder's row, the quick filter's Tags chip, the applied
	// chips' names. Live mode (this is the reader's own list): the
	// collection's tags and its parent's, my private tags, and the item-tag
	// titles the feed carries. The Tags chip is offered once there is a tag
	// to pick.
	const { tags: tagVocabulary, loaded: tagVocabularyLoaded } = useCollectionTags(collectionId);
	const myPrivateTags = useOptionalTags()?.tags || NO_TAGS;
	const feedItemTagTitles = useMemo(() => itemTagTitlesOf(items), [items]);
	const tagsField = useMemo(() => tagsFilterField({
		tags: tagVocabulary,
		privateTags: myPrivateTags,
		itemTitles: feedItemTagTitles,
		mode: 'live',
		loaded: tagVocabularyLoaded,
		quickFilter: tagVocabulary.length > 0 || myPrivateTags.length > 0,
	}), [tagVocabulary, myPrivateTags, feedItemTagTitles, tagVocabularyLoaded]);
	const filterFields = useMemo(
		() => [
			...buildFilterFields({ cards, cardIds: listCardIds, getElementsForCard, facetCardKeys, elementSets, hopTargets, family: familyMembers }),
			tagsField,
		],
		[cards, listCardIds, getElementsForCard, facetCardKeys, elementSets, hopTargets, familyMembers, tagsField]
	);

	// "Filter by Alice" on a tile should reach every card that mentions her:
	// this is how a value five components down learns that its element belongs
	// to a facet (it rides the FilterByValueProvider, not a prop chain).
	const facetFor = useMemo(() => createFacetResolver(filterFields), [filterFields]);

	const favorites = useOptionalFavorites();
	const cardOptions = useMemo(
		() => buildCardHierarchyList(cards || []).map(({ card, depth, ancestorNames }) => {
			const value = normalizeId(card?._id);
			if (!value || (collectionCardIds && !collectionCardIds.has(value))) return null;
			return {
				value,
				card,
				depth,
				// A card this collection uses but does not own (a parent's,
				// inherited) reads qualified by its owner; its own do not.
				maxChars: CARD_NAME_ROW_CHARS,
				label: <CardOptionLabel card={card} depth={depth} collection={cardOwnerOf(card)} maxChars={CARD_NAME_ROW_CHARS} />,
				// A sub-collection's card answers to its collection's name
				// too: typing "health" finds Health's cards.
				searchText: `${cardSearchText(card, ancestorNames)} ${cardHomeOf(value, collection)?.name || ''}`.trim(),
			};
		}).filter(Boolean),
		[cards, collectionCardIds, collection]
	);

	// Manager-pinned cards for this collection, shared by every user of it.
	const collectionFavoriteIds = useMemo(
		() => getCollectionFavoriteCardIds(collection),
		[collection]
	);

	// The borrowed cards, while the merged list is on: their own group,
	// "Linked", each saying which collection it comes from.
	const linkedCardOptions = useMemo(() => {
		if (!linkedOn || !borrowedCards.length) return [];
		return buildCardHierarchyList(borrowedCards).map(({ card, depth, ancestorNames }) => {
			const value = normalizeId(card?._id);
			const from = borrowedFromOf(card);
			if (!value || (collectionCardIds && collectionCardIds.has(value))) return null;
			return {
				value,
				card,
				depth,
				linked: true,
				// The qualified card badge: the lending collection's tile and
				// name, then the card.
				label: (
					<CardOptionLabel card={card} depth={depth} collection={sourceQualifierOf(from)} />
				),
				searchText: `${cardSearchText(card, ancestorNames)} ${from?.name || ''}`,
			};
		}).filter(Boolean);
	}, [linkedOn, borrowedCards, collectionCardIds]);

	// A `card` condition in the query (the "Linked from" door writes `card in
	// (…)` by id — a borrowed card's key may collide with one of ours) reads
	// as the cards' badges in its chip: by id first, any card this page
	// knows (a lent one included), then by key among this collection's own.
	// The same fixed order as the scope, so a key names one card in both.
	const cardByRef = useMemo(
		() => createCardResolver([...(cards || []), ...(borrowedCards || [])], { memberCardIds: collectionCardIds, ...keyTiers }),
		[cards, borrowedCards, collectionCardIds, keyTiers]
	);
	const resolveFilterCard = useCallback(value => {
		const text = `${value || ''}`;
		return cardsById?.[normalizeId(text)] || cardByRef(text);
	}, [cardsById, cardByRef]);

	// Both kinds of favorite pin to the top as one group; linked collections'
	// cards follow in theirs.
	const groupedCardOptions = useMemo(() => {
		// At a parent, each sub-collection's own cards follow in a group of
		// their own, headed by its badge (docs/plans/sub-collections.md §5.5).
		const homed = new Map();
		const ownOptions = [];
		cardOptions.forEach(opt => {
			const home = cardHomeOf(opt.value, collection);
			if (!home) { ownOptions.push(opt); return; }
			if (!homed.has(home._id)) homed.set(home._id, { home, options: [] });
			homed.get(home._id).options.push({ ...opt, depth: 0, label: <CardOptionLabel card={opt.card} depth={0} maxChars={CARD_NAME_ROW_CHARS} /> });
		});
		const own = groupFavoriteCardOptions(ownOptions, id => resolveFavoriteKind(
			id,
			collectionFavoriteIds,
			favorites ? cardId => favorites.isFavoriteCard(collectionId, cardId) : null
		));
		if (!linkedCardOptions.length && !homed.size) return own;
		const ownGroups = own.length && own.every(opt => Array.isArray(opt?.options))
			? own
			: own.length ? [{ label: homed.size ? `In ${collection?.name || 'this collection'}` : 'This collection', options: own }] : [];
		const familyGroups = [...homed.values()].map(({ home, options }) => ({
			key: `sub:${home._id}`,
			title: `In ${home.name}`,
			label: <RecordBadge kind="collection" record={home} title={home.name} size="xs" />,
			options,
		}));
		return [
			...ownGroups,
			...familyGroups,
			...(linkedCardOptions.length ? [{ label: 'Linked', options: linkedCardOptions }] : []),
		];
	}, [cardOptions, favorites, collectionId, collection, collectionFavoriteIds, linkedCardOptions]);

	// Drop a stale card filter (e.g. from an old link) that isn't part of
	// this list. The borrowed cards are known only once the links are read,
	// so a borrowed card in the address waits for that answer.
	useEffect(() => {
		if (linked.loading) return;
		if (cardFilter && listCardIds && !listCardIds.has(cardFilter)) {
			update({ card: undefined, cols: undefined });
		}
	}, [cardFilter, listCardIds, update, linked.loading]);

	// Element-based sort options only make sense when every visible item is
	// the same card type: either an explicit filter, or a feed that happens
	// to hold a single type.
	const uniqueCardInFiltered = useMemo(() => {
		if (cardFilter) return cardFilter;
		const ids = new Set((items || []).map(item => normalizeId(item.card_id)).filter(Boolean));
		return ids.size === 1 ? Array.from(ids)[0] : undefined;
	}, [cardFilter, items]);

	// What the list can be sorted by, the change handler, and the fallback
	// ordering of the loaded rows (hooks/useListSort). The element options only
	// make sense when every visible item is the same card type: either an
	// explicit filter, or a feed that happens to hold a single type.
	const sort = useListSort({
		order: sortOrder,
		writeSort: update,
		cardId: uniqueCardInFiltered,
		items,
		getElementsForCard,
		cardPrimaryDate,
		chosen: sortChosen,
		ready: !cardsLoading,
	});
	const { sortMeta, primaryDateGetter, sortedItems } = sort;

	// ---- Worklist mode (hooks/useWorklistMode): the latch effect and what
	//      the latched card decides — called once the feed's items are sorted ----
	// The first page failed — a new feed's, or a refresh's: nothing on screen
	// answers the question now being asked (a new feed's old rows are already
	// gone), so the list, its count and a worklist's pass are not drawn.
	const firstPageFailed = itemsLoadError?.page === 0;
	const {
		worklistFilter,
		worklistConfig,
		worklistCard,
		worklistElements,
		worklistBlocked,
		blockedBody: worklistBlockedBody,
		worklistItems,
		toolbarProps: worklistToolbar,
	} = useWorklistMode({
		feed: worklistFeed,
		viewMode,
		uniqueCardInFiltered,
		collectionId,
		cardFilter,
		cardsById,
		borrowedCardIds,
		getElementsForCard,
		query,
		worklistFeedError,
		firstPageFailed,
		itemsLoadError,
		itemsLoading,
		sortedItems,
		filterFields,
		retryItems,
		changeViewMode,
	});

	// ---- Grid separators: the period, and which groups are collapsed ----

	// Grouping is grid-only and date-sort-only: separating a list sorted by
	// anything else would cut it at meaningless places.
	const isDateSort = sortMeta?.dataType === 'date' || sortMeta?.dataType === 'date-time';
	const groupingApplies = isDateSort && effectiveView === 'grid';
	const allowSubDay = subDayAllowed(sortMeta);
	// What the control shows and the feed obeys: a sub-day period over a
	// date-only sort reads as the day, and the URL is left untouched so
	// sorting back to a timestamp restores what the user asked for.
	const effectiveGroup = resolveGroupToken(groupToken, allowSubDay);

	// Whichever date is sorting is the date that groups.
	const groupDateOf = useCallback((item) => {
		if (primaryDateGetter) return primaryDateGetter(item);
		if (BASE_SORT_OPTIONS.some(base => base.value === sortField)) {
			return item?.[sortField] || item?.createdAt || item?.created_at;
		}
		return item?.elements?.[sortField];
	}, [primaryDateGetter, sortField]);

	const itemGroups = useMemo(() => {
		if (!groupingApplies) return null;
		return buildItemGroups(sortedItems, effectiveGroup, groupDateOf);
	}, [sortedItems, groupingApplies, effectiveGroup, groupDateOf]);

	// Two bits survive a reload (NO DATE, and whether "Collapse all" was the
	// last bulk action); individual groups are a session habit, held as the
	// set of keys whose state DIFFERS from that default.
	const [collapseDefaults, setCollapseDefaults] = useState(() => readStoredGroupCollapse(collectionId));
	// A bucket key does not name the period that made it: '2026-09-13' is a
	// day, the head of a 3-day run AND a week. So the flipped keys are held
	// WITH the period they were flipped under and read as nothing under any
	// other — folding a day away must never arrive pre-folded as a week
	// nobody touched. (Switching back restores what was flipped there.)
	const [flipped, setFlipped] = useState({ token: effectiveGroup, keys: NO_FLIPPED_GROUPS });
	const flippedGroups = flipped.token === effectiveGroup ? flipped.keys : NO_FLIPPED_GROUPS;
	const setFlippedGroups = useCallback(
		keys => setFlipped({ token: effectiveGroup, keys }),
		[effectiveGroup]
	);
	useEffect(() => {
		setCollapseDefaults(readStoredGroupCollapse(collectionId));
		setFlipped(current => ({ ...current, keys: NO_FLIPPED_GROUPS }));
	}, [collectionId]);

	const defaultCollapsedFor = useCallback(
		key => collapseDefaults.all || (key === UNDATED_KEY && collapseDefaults.undated),
		[collapseDefaults]
	);
	const isGroupCollapsed = useCallback(
		key => (flippedGroups.has(key) ? !defaultCollapsedFor(key) : defaultCollapsedFor(key)),
		[flippedGroups, defaultCollapsedFor]
	);

	const persistCollapseDefaults = useCallback((next) => {
		setCollapseDefaults(next);
		writeStoredGroupCollapse(collectionId, next);
	}, [collectionId]);

	// Shift-click is "just this one": every other group folds away.
	const toggleGroup = useCallback((key, event) => {
		if (event?.shiftKey) {
			persistCollapseDefaults({ all: true, undated: true });
			setFlippedGroups(new Set([key]));
			return;
		}
		const collapsed = isGroupCollapsed(key);
		const next = new Set(flippedGroups);
		if (key === UNDATED_KEY && !collapseDefaults.all) {
			// NO DATE has its own remembered bit; keep that the truth.
			persistCollapseDefaults({ ...collapseDefaults, undated: !collapsed });
			next.delete(key);
			setFlippedGroups(next);
			return;
		}
		if (next.has(key)) next.delete(key); else next.add(key);
		setFlippedGroups(next);
	}, [isGroupCollapsed, collapseDefaults, persistCollapseDefaults, flippedGroups, setFlippedGroups]);

	const setAllCollapsed = useCallback((collapsed) => {
		persistCollapseDefaults({ all: collapsed, undated: collapsed });
		setFlippedGroups(NO_FLIPPED_GROUPS);
	}, [persistCollapseDefaults, setFlippedGroups]);

	// ↑↓ move through what is on screen: an item inside a collapsed group is
	// not on screen. On the map it is the list beside the map, in its order.
	const navigableItems = useMemo(() => {
		if (mapOn) return mapOrder;
		if (boardOn) return boardWalk;
		if (!itemGroups) return sortedItems;
		return itemGroups.flatMap(group => (isGroupCollapsed(group.key) ? [] : group.items));
	}, [mapOn, mapOrder, boardOn, boardWalk, itemGroups, sortedItems, isGroupCollapsed]);

	// ---- Selection & the detail pane ----

	// The items the open one is looked up in: the feed, or on the map the
	// map's own items (a pin can open an item the feed has not paged to) and
	// then the feed's — which holds the first page of the same question, so
	// the open item does not drop out while a new question's map loads.
	const selectionPool = useMemo(
		() => (mapOn ? [...mapItems.items, ...sortedItems] : boardOn ? [...boardItems.items, ...sortedItems] : sortedItems),
		[mapOn, mapItems.items, boardOn, boardItems.items, sortedItems]
	);
	const selectedIndex = useMemo(
		() => (selectedItemId
			? selectionPool.findIndex(item => normalizeId(item?._id) === selectedItemId)
			: -1),
		[selectionPool, selectedItemId]
	);

	// Is anything on screen actually showing the selected item? A phone
	// expands it INSIDE the grid, and a collapsed group's grid is unmounted —
	// so an item a fold is hiding has nothing to expand in place, exactly
	// like one the feed has not loaded.
	const selectedShownInFeed = useMemo(
		() => selectedIndex >= 0 && navigableItems.some(item => normalizeId(item?._id) === selectedItemId),
		[selectedIndex, navigableItems, selectedItemId]
	);

	// ↑/↓ held down moves the selection at key-repeat speed. What is asked of
	// the SERVER about the selected item (the item alone when the feed does
	// not hold it, the view record) waits until the selection has rested, so
	// only the item the cursor stops on costs a request. The pane's own
	// sections wait the same way (ItemReadView).
	const settledItemId = useSettledValue(selectedItemId, SELECTION_SETTLE_MS);
	const selectionSettled = settledItemId === selectedItemId;

	// A deep link (?item=) may point at an item beyond the loaded feed; fetch
	// it alone so the pane can still open. A selection that moves on first
	// cancels the request it no longer needs.
	// The same read answers "is it still there?" after a Refresh (KPR-1346):
	// a 404 or 403 is remembered as `selectedMissing` (the id it answered), and
	// the pane says the item is gone instead of showing an old copy.
	// `paneRefreshKey` moves on every Refresh, so an open item the feed does
	// not hold is read again too.
	const [fetchedSelected, setFetchedSelected] = useState(null);
	const [selectedMissing, setSelectedMissing] = useState(null);
	// The id a read of the item alone failed on for any other reason (a
	// network error, a 5xx): a phone says so rather than leaving the list as
	// if nothing had been asked (KPR-1244).
	const [selectedFailed, setSelectedFailed] = useState(null);
	const [paneRefreshKey, setPaneRefreshKey] = useState(0);
	useEffect(() => {
		if (!selectedItemId || selectedIndex >= 0) {
			setFetchedSelected(null);
			setSelectedMissing(null);
			setSelectedFailed(null);
			return undefined;
		}
		if (!selectionSettled) return undefined;
		const controller = new AbortController();
		api.get(`${API_ITEMS_URL}/${selectedItemId}`, { signal: controller.signal })
			.then(({ data }) => {
				if (controller.signal.aborted) return;
				setFetchedSelected(data || null);
				setSelectedMissing(null);
				setSelectedFailed(null);
			})
			.catch((error) => {
				if (controller.signal.aborted) return;
				setFetchedSelected(null);
				const status = error?.response?.status;
				const unreadable = status === 404 || status === 403;
				setSelectedMissing(unreadable ? selectedItemId : null);
				setSelectedFailed(unreadable ? null : selectedItemId);
			});
		return () => controller.abort();
	}, [selectedItemId, selectedIndex, selectionSettled, paneRefreshKey]);

	// Only ever the item that was asked for: one fetched for an earlier
	// selection never opens under a later one's id.
	const selectedLive = selectedIndex >= 0
		? selectionPool[selectedIndex]
		: (fetchedSelected && normalizeId(fetchedSelected._id) === selectedItemId ? fetchedSelected : null);
	// While the item is being read again on its own (a Refresh took it out of
	// the feed), the copy last shown holds the pane, so it does not close and
	// reopen; once the answer is in, that copy is never shown again.
	const lastShownRef = useRef(null);
	const selectedGone = Boolean(selectedItemId) && selectedMissing === selectedItemId && !selectedLive;
	const selectedItem = selectedLive
		|| (!selectedGone && selectedItemId && selectedIndex < 0 && lastShownRef.current
			&& normalizeId(lastShownRef.current._id) === selectedItemId && fetchedSelected === null
			? lastShownRef.current
			: null);
	lastShownRef.current = selectedLive || (selectedGone ? null : lastShownRef.current);

	// Opening the detail pane is a view — once the selection has rested on
	// it; recordView throttles repeats itself.
	useEffect(() => {
		if (selectionSettled && selectedItemId && selectedItem) recordView('item', selectedItemId);
	}, [selectionSettled, selectedItemId, selectedItem]);


	// The item this page's own people picked (a tap, an arrow key): it is under
	// their finger or cursor already, so a phone does not scroll to it. An item
	// that arrives by address instead (the palette, a recent, a shared link) is
	// one they cannot see yet (KPR-1244).
	const pickedHereRef = useRef(null);
	const selectItem = useCallback(item => {
		const id = item ? normalizeId(item._id) : undefined;
		pickedHereRef.current = id || null;
		setSelectedItemId(id);
	}, [setSelectedItemId]);

	// compact (§18): the selected card grows into the detail in place, no pane;
	// the toolbar, board, table, sort and saved filters follow this one flag.
	const { compact: isMobile } = useWindowClass();

	// A Delete confirmed from a card, the table, or the pane: drop the row from
	// the feed on the spot and let go of the selection if it was the one.
	const removeMapItem = mapItems.removeItem;
	const removeBoardItem = boardItems.removeItem;
	const handleItemDeleted = useCallback((id) => {
		removeItem(id);
		removeMapItem(id);
		removeBoardItem(id);
		if (selectedItemId === id) setSelectedItemId(undefined);
	}, [removeItem, removeMapItem, removeBoardItem, selectedItemId, setSelectedItemId]);

	// An item changed from the pane (a tag, its place) or moved on the board
	// takes its old self's place in the feed, on the map and on the board alike.
	const replaceMapItem = mapItems.replaceItem;
	const replaceBoardItem = boardItems.replaceItem;
	const replaceItemEverywhere = useCallback((updated) => {
		replaceItem(updated);
		replaceMapItem(updated);
		replaceBoardItem(updated);
	}, [replaceItem, replaceMapItem, replaceBoardItem]);

	// A linked row is read-only here, whoever reads it: it is edited in its
	// own collection, by that collection's people.
	const canEditItem = useCallback(
		item => !isReadOnlyRow(item, collection) && (!collection || canWriteCard(collection, normalizeId(item?.card_id))),
		[collection]
	);

	// ---- Multi-selection & "apply to selected" (hooks/useItemsBulkState) ----
	//
	// The state lives in the page, above both list bodies, keyed by item id
	// (the hook says why); the five surfaces it drives are ItemsBulkSurfaces.
	const navigate = useNavigate();
	const bulk = useItemsBulkState({
		collection,
		collectionId,
		cardsById,
		cardFilter,
		feedQuery,
		review,
		sortField,
		sortDirection,
		linkedOn,
		totalCount,
		viewMode,
		mapOn,
		boardOn,
		sortedItems,
		navigableItems,
		resolveElements,
		resolveLookupValue,
		removeItem,
		refreshItems,
		refreshCollection,
		message,
		navigate,
		searchParams: viewSearchParams,
		setSearchParams: setViewSearchParams,
	});
	const { selection, selectionEnabled, selectionDisabledFor, handleToggleSelect, handleLongPressSelect } = bulk;
	// The keydown handler is registered once on `document` and must not be
	// torn down and rebuilt on every selection change — so it reads the
	// selection through a ref rather than closing over it.
	const selectionRef = useRef(selection);
	selectionRef.current = selection;
	// Same reason, for the two facts the handler asks about the SELECTION
	// FEATURE rather than the selection: whether it is offered at all, and
	// whether a given row may join it. Read live, so a handler registered
	// before the first page of items landed still answers correctly.
	const selectRef = useRef({ enabled: false, disabledFor: () => null });
	selectRef.current = { enabled: selectionEnabled, disabledFor: selectionDisabledFor };
	// And the two facts the keyboard map reads about the FEED: what is on
	// screen, and which row the cursor is on. Kept in a ref for the same
	// reason — one listener on `document`, registered once, always answering
	// with what is true now rather than with whatever was true when React
	// last flushed its effects.
	const feedRef = useRef({ items: [], selectedItemId: undefined });
	feedRef.current = { items: navigableItems, selectedItemId };

	// Accept / Reject from the detail pane (IntakeReviewBar). An accepted
	// item leaves the review queue and joins the ordinary list; a rejected
	// one is deleted; 'stale' means somebody else decided it first.
	const refreshMap = mapItems.refresh;
	const handleIntakeReviewed = useCallback((decision) => {
		if (decision === 'reject' && selectedItemId) handleItemDeleted(selectedItemId);
		refreshItems();
		// On the map too: an accepted submission leaves a review-queue map.
		if (mapOn) refreshMap();
		refreshCollection?.();
	}, [selectedItemId, handleItemDeleted, refreshItems, mapOn, refreshMap, refreshCollection]);

	// The person's Refresh (KPR-1346; standards §5 "Refresh"): the same list
	// read again in place. Every page on screen is re-read (reload), so the
	// person keeps their place; the filter, card, sort, search, grouping and
	// linked switch are untouched, so the address does not change. The map
	// reads its own items again; the open item's pane re-reads its sections
	// (and the item itself, when the feed does not hold it); a selected item
	// that left the list leaves the selection, the rest stay selected.
	const handleRefresh = useCallback(() => {
		setPaneRefreshKey(key => key + 1);
		if (mapOn) refreshMap();
		return reloadItems().then((result) => {
			const gone = result?.removedIds || [];
			const current = selectionRef.current;
			if (gone.length && current && !current.allMatching) {
				const held = new Set(current.ids || []);
				const drop = gone.filter(id => held.has(id));
				if (drop.length) current.forget(drop);
			}
			return result;
		});
	}, [mapOn, refreshMap, reloadItems]);

	// The inline detail a phone renders inside the selected card / table row.
	const renderExpanded = useCallback(item => (
		<ItemDetailPane
			variant="inline"
			item={item}
			card={cardsById?.[normalizeId(item?.card_id)] || null}
			elementDefs={resolveElements(item?.card_id)}
			resolveLookupValue={resolveLookupValue}
			collectionId={itemHomeCollectionId(item, collectionId)}
			collection={isReadOnlyRow(item, collection) ? null : collection}
			onClose={() => selectItem(null)}
			canEdit={canEditItem(item)}
			canManage={isReadOnlyRow(item, collection) ? false : canManage}
			readOnlyRow={isReadOnlyRow(item, collection)}
			cardsById={cardsById}
			onDeleted={handleItemDeleted}
			onIntakeReviewed={handleIntakeReviewed}
			onItemUpdated={replaceItemEverywhere}
			reloadKey={paneRefreshKey}
		/>
	), [cardsById, resolveElements, resolveLookupValue, collectionId, collection, selectItem, canEditItem, canManage, handleItemDeleted, handleIntakeReviewed, replaceItemEverywhere, paneRefreshKey]);

	// The pane's width (KPR-1643); `[` and `]` step it through a ref.
	const paneWidth = usePaneWidth(collectionId);
	const paneStepRef = useRef(paneWidth.step);
	paneStepRef.current = paneWidth.step;

	// One item up or down the list (↑/↓, the pane's ‹ ›; KPR-1643).
	const stepSelection = useListStep({ feedRef, selectRef, selectionRef, selectItem });

	// The platform keyboard map and its shortcuts sheet (hooks/useItemsListKeys);
	// Enter asks the pane, which edits in place (KPR-1644).
	const paneRef = useRef(null);
	useItemsListKeys({
		collectionId, searchId: ITEMS_SEARCH_ID, mapOn, boardOn, selectionEnabled, isMobile,
		feedRef, selectRef, selectionRef, paneStepRef, paneRef, stepSelection, selectItem,
	});

	// ---- Infinite scroll (components/items/ListScrollLoader) ----
	// Every group folded away leaves nothing on screen that MORE items could
	// fill: the loader stands down there and a plain button takes its place.
	const nothingToFill = Boolean(itemGroups?.length) && navigableItems.length === 0;
	// (`firstPageFailed`, the first page failing, is read beside the worklist
	// hook above, which needs it first.)

	const [tileLayoutOpen, setTileLayoutOpen] = useState(false);
	const [density, setDensity] = useRowDensity(collectionId);

	// How each card of this collection wants its tile arranged, cascaded
	// card -> collection -> user by the API. Absent for a card nobody has
	// arranged, which is the automatic pick.
	const { layouts: tileLayouts, refresh: refreshTileLayouts } = useTileLayouts(collectionId, cardsVersion);

	const isLoading = itemsLoading || cardsLoading || collectionLoading;
	// A first page that failed is not an empty collection: the Retry row
	// says what happened instead of "No items yet".
	const isEmpty = !isLoading && !itemsLoadError && totalCount === 0 && sortedItems.length === 0;

	// ---- Opening an item by address on a phone (KPR-1244) ----
	// A phone has no side pane: the item opens as the read view inside its own
	// tile. When the tile is not on screen — the feed has not paged to it, a
	// filter keeps it out, or a collapsed group is hiding it — the item is
	// drawn as a tile of its own at the top of the list, opened, so the person
	// lands ON it instead of on a list with no sign of what they chose. (The
	// board and the map expand their own cards, and a worklist is a pass, not
	// a list of tiles: they keep the sheet.) It waits for the first page, so
	// an item that IS in the feed opens in its place and does not jump.
	const phoneOpensInline = isMobile && !mapOn && !boardOn && viewMode !== 'worklist';
	const pinnedOpen = phoneOpensInline && !isLoading && Boolean(selectedItem) && !selectedShownInFeed;
	const phoneHoldsOpen = phoneOpensInline && Boolean(selectedItem) && (selectedShownInFeed || pinnedOpen);
	// The notice a phone gives for an item it could not open, in the list's own
	// place: gone (deleted, or not readable by this person) or not loaded.
	const phoneNotice = isMobile && selectedItemId && !selectedItem
		? (selectedGone ? 'gone' : selectedFailed === selectedItemId ? 'failed' : null)
		: null;

	// Scroll to it once per address, as soon as its tile is on screen: the
	// feed's rows can be in hand while the list still draws its loading rows
	// (the skeleton is held a minimum time), so this waits for the tile itself.
	// A phone scrolls the opened tile to the top of the window (the tile's
	// scroll margin clears the sticky date header); closing it re-arms the next.
	const scrolledToRef = useRef(null);
	useEffect(() => {
		if (!selectedItemId) {
			scrolledToRef.current = null;
			pickedHereRef.current = null;
			return undefined;
		}
		if (!phoneHoldsOpen || scrolledToRef.current === selectedItemId) return undefined;
		if (pickedHereRef.current === selectedItemId || !/^[\w-]+$/.test(selectedItemId)) {
			scrolledToRef.current = selectedItemId;
			return undefined;
		}
		const find = () => document.querySelector(`[data-item-id="${selectedItemId}"]`);
		const arrive = (node) => {
			scrolledToRef.current = selectedItemId;
			node.scrollIntoView?.({ block: 'start' });
		};
		const present = find();
		if (present) {
			arrive(present);
			return undefined;
		}
		let giveUp;
		const watcher = new MutationObserver(() => {
			const node = find();
			if (!node) return;
			watcher.disconnect();
			clearTimeout(giveUp);
			arrive(node);
		});
		watcher.observe(document.querySelector('.items-page') || document.body, { childList: true, subtree: true });
		giveUp = setTimeout(() => watcher.disconnect(), TILE_WAIT_MS);
		return () => {
			watcher.disconnect();
			clearTimeout(giveUp);
		};
	}, [selectedItemId, phoneHoldsOpen]);
	// The card's worklist filter narrows the list as surely as the reader's own
	// does, and an empty list is a claim about the collection either way. An
	// unresolvable condition compiles to "matches nothing" server-side rather
	// than to a 400, so a pass with outstanding work can come back empty — and
	// "No items yet", with an Add item button under it, is how a duplicate
	// record gets filed.
	const isFiltered = Boolean(query || cardFilter || worklistFilter || review);
	// No cards at all (own, inherited, or a sub-collection's) is a different
	// fact from "no items": there is nothing to add yet, so the way in is a
	// card (KPR-322). Only on an UNFILTERED empty list — a filter matching
	// nothing keeps "No items match" — and only once the cards have answered:
	// a cards fetch that failed knows nothing, and must not tell a manager
	// with ten cards to create their first one.
	const noCards = isEmpty && !isFiltered && !cardsError && !collectionPending
		&& collectionHasNoCards(collection, cards);

	const selectedCard = selectedItem
		? (cardsById?.[normalizeId(selectedItem?.card_id)] || null)
		: null;

	// Shared by every list body: the feed's selection, the inline detail on
	// phones, and the action callbacks.
	const listProps = {
		cardsById,
		tileLayouts,
		resolvePrimaryDate: cardPrimaryDate,
		itemsById,
		collectionId,
		collection,
		getElementsForCard,
		selectedItemId,
		onSelectItem: selectItem,
		expandedItemId: isMobile ? selectedItemId : null,
		renderExpanded: isMobile ? renderExpanded : undefined,
		onItemDeleted: handleItemDeleted,
		// ⋯ Tag… on a tile or a row: the item as it stands after the change
		// takes the row's place in the feed.
		onItemUpdated: replaceItem,
		// One selection model, shared by the table, the grid and every
		// group's grid — because it lives here and not in any of them.
		selectedIds: selectionEnabled ? selection.selectedIds : null,
		onToggleSelect: handleToggleSelect,
		onLongPressSelect: handleLongPressSelect,
		// The TABLE reports antd's own change as a whole key set (its shift
		// range fires no per-row callback); the GRID reports one tile at a
		// time. Both land on the same state.
		onSelectionChange: selection.replace,
		onSelectionAnchor: selection.setAnchor,
		selectionMode: selection.selectionMode,
		// Anything checked: every tile shows its checkbox, not only the hovered one.
		selectionActive: selectionEnabled && selection.active,
		selectionDisabledFor,
	};

	// A MANUAL ORDER (KPR-1396, utils/rank.js): a list sorted by a rank
	// element reorders by drag (the table here; the board keeps its own) and
	// by Move up / Move down / Move to top in each row's ⋯.
	const rankSort = useMemo(
		() => rankSortOf({ sortField: sortOrder.sortField, sortDirection: sortOrder.sortDirection, sortMeta }),
		[sortOrder.sortField, sortOrder.sortDirection, sortMeta]
	);
	const rankOrder = useRankMove({
		rank: viewMode === 'table' ? rankSort : null,
		rows: sortedItems,
		collection,
		collectionId,
		cardsById,
		getElementsForCard,
		onItemUpdated: replaceItemEverywhere,
		onReload: handleRefresh,
	});

	const listBody = viewMode === 'worklist' && worklistConfig ? (
		<ItemWorklist
			// A pass writes every row it lists; a linked collection's records
			// are never this collection's to write.
			items={worklistItems}
			worklist={worklistConfig}
			card={worklistCard}
			elements={worklistElements}
			collectionId={collectionId}
			user={currentUser}
			cardsById={cardsById}
			getElementsForCard={getElementsForCard}
			itemsById={itemsById}
			onSaved={() => refreshItems()}
		/>
	) : viewMode === 'table' ? (
		<ItemsTable
			{...listProps}
			items={sortedItems} loading={isLoading} tableLayouts={tableLayoutsShown}
			headerMenu={{ sort: sort.levelsProps, onColumnsChanged: () => { setSavedColumns([]); refreshTableLayouts(); }, filter: { query, fields: filterFields, onChange: setQuery, openBuilder: () => document.getElementById(ITEM_FILTER_TRIGGER_ID)?.click() } }}
			// Editing in place: a cell's settled item takes its row's place.
			onItemUpdated={replaceItem}
			onReload={refreshItems}
			showCollectionColumn={showCollectionColumn}
			rankOrder={rankOrder.active ? rankOrder : null}
		/>
	) : itemGroups ? (
		itemGroups.map((group) => {
			const collapsed = isGroupCollapsed(group.key);
			return (
				<section key={group.key} className="items-page__day-group">
					<GroupHeader
						label={group.label}
						count={group.items.length}
						sticky
						collapsible
						collapsed={collapsed}
						onToggle={event => toggleGroup(group.key, event)}
					/>
					{/* Collapsed: the header alone. The grid is unmounted, not hidden. */}
					{collapsed ? null : (
						<ItemCardsGrid
							{...listProps}
							items={group.items}
							loading={false}
							emptyText={null}
						/>
					)}
				</section>
			);
		})
	) : (
		<ItemCardsGrid
			{...listProps}
			items={sortedItems}
			loading={isLoading}
			emptyText={null}
		/>
	);

	// ---- The toolbar's pieces, laid out per breakpoint below ----

	const cardFilterSelect = (
		<CardFilterSelect
			options={groupedCardOptions}
			value={cardFilter}
			onChange={value => update({ card: value, cols: undefined })}
			collectionId={collectionId}
			collectionFavoriteIds={collectionFavoriteIds}
		/>
	);

	// A parent's list is its family; this narrows it to one sub-collection by
	// writing `collection = <id>` into `q` (shareable, savable, exportable
	// with the rest of the query). Offered only when there are sub-collections.
	const subCollectionValue = useMemo(() => {
		if (!subCollections.length) return undefined;
		const value = readEqualsTerm(parseForBuilder(query).ast, 'collection');
		return subCollections.some(sub => sub._id === value) ? value : undefined;
	}, [subCollections, query]);
	const subCollectionSelect = subCollections.length ? (
		<Select
			allowClear
			className="items-page__sub-collection-filter"
			placeholder="All sub-collections"
			aria-label="Sub-collection"
			value={subCollectionValue}
			onChange={value => {
				const next = withEqualsTerm(parseForBuilder(query).ast, value, 'collection');
				setQuery(next ? serialize(next) : undefined);
			}}
			options={subCollections.map(sub => ({
				value: sub._id,
				searchText: sub.name,
				label: <RecordBadge kind="collection" record={sub} title={sub.name} size="xs" />,
			}))}
			showSearch
			filterOption={optionSearchFilter}
			popupMatchSelectWidth={false}
		/>
	) : null;

	// The search term is the box's: its chip is hidden because the box shows
	// it. With no box (the gate), a `text` condition in the address stays a
	// chip - dismissible - so nothing narrows the list out of sight.
	const filterChips = query ? (
		<ItemFilterChips
			query={query}
			fields={filterFields}
			onChange={setQuery}
			className="items-page__filter-chips"
			resolveCard={resolveFilterCard}
			removedIds={removedIds}
			hiddenPaths={searchAvailable ? SEARCH_PATHS : NO_PATHS}
		/>
	) : null;

	const searchBox = searchAvailable ? (
		<ListSearchBox search={search} id={ITEMS_SEARCH_ID} />
	) : null;

	// The TABLE's columns are a property of one card too, offered where the
	// table is showing exactly one: "Columns…" chooses, orders and freezes
	// them, at whichever tier the reader may write (like the tile's editor).
	const [tableColumnsOpen, setTableColumnsOpen] = useState(false);
	const tableColumnsCard = viewMode === 'table' && uniqueCardInFiltered
		? (cardsById?.[uniqueCardInFiltered] || null)
		: null;
	// What the table is showing now — what an unarranged tier starts from.
	const tableColumnsNow = useMemo(() => {
		if (!tableColumnsCard) return [];
		const defs = getElementsForCard(tableColumnsCard._id) || [];
		const spec = cardPrimaryDate(tableColumnsCard._id);
		const primaryElement = spec?.kind === 'ordered' ? spec.entries?.[0]?.element?.name : spec?.element?.name;
		const defaults = defaultTableColumns(defs, { primaryElement, primaryIsUpdated: spec?.kind === 'updated' });
		return resolveTableColumns(tableLayoutsShown?.[normalizeId(tableColumnsCard._id)]?.body, defs, defaults);
	}, [tableColumnsCard, getElementsForCard, cardPrimaryDate, tableLayoutsShown]);
	// The saved filter menu and its Update / Save as new / Revert (KPR-1642).
	const savedFilterControls = useSavedFilterControls({
		collectionId, savedFilterId, canManage, isMobile, fields: filterFields, adoptions, update, onStoredGroup: setStoredGroup,
		// What is on screen, as the person chose it, and what a save sends:
		// the order the feed runs, the period where grouping applies, the board.
		screen: { query, cardFilter, viewMode, savedColumns, sortField: urlSortField, sortDirection: urlSortDirection, sortThen: urlSortThen, groupToken, board: boardAsked },
		draft: {
			query, sortField, sortDirection, sortThen: sortOrder.sortThen, groupBy: groupingApplies ? effectiveGroup : null,
			board: boardOn && board ? storedBoardOf(board) : null, cardFilter, viewMode, savedColumns,
			tableColumns: tableColumnsCard ? tableColumnsNow : null, elementDefs: tableColumnsCard ? getElementsForCard(tableColumnsCard._id) : null,
		},
		boardText: boardOn && board ? boardText : '',
	});

	// The warning's Edit filter door opens the builder the way `/` does: by
	// its trigger, so the popover (or a phone's drawer) opens holding the
	// filter on screen.
	const openFilterBuilder = useCallback(() => {
		document.getElementById(ITEM_FILTER_TRIGGER_ID)?.click();
	}, []);

	const filterBuilder = (
		<ItemFilterBuilder
			fields={filterFields}
			query={query}
			removedIds={removedIds}
			onApply={setQuery}
			collectionId={collectionId}
			savedQueries={savedFilterControls.savedQueries}
			// Sections the field menu into "in view" / "other cards". It never
			// narrows it: the builder is the door that expresses anything,
			// including a condition on the card you are about to switch to.
			scopeCardIds={scopeCardIds}
		/>
	);

	// The quick-filter chips: one per facet, one per element an editor promoted
	// with `quickFilter`. They sit BETWEEN + Filter and the saved views —
	// after the door that can express anything, before the views that name a
	// whole query — because each one is a single question asked in one click.
	const hasQuickFilters = quickFilterFields(filterFields).length > 0;
	const quickFilters = hasQuickFilters ? (
		<QuickFilterChips
			fields={filterFields}
			query={query}
			collectionId={collectionId}
			onApply={applyValueFilter}
			onChange={setQuery}
			scopeCardIds={scopeCardIds}
			pinned={pinnedQuickFilters}
			hidden={hiddenQuickFilters}
			// A phone toolbar has room for three; a desktop one for six. The
			// rest go behind "More filters" — and an ACTIVE chip is never in
			// there, whatever the cap.
			limit={isMobile ? QUICK_FILTER_LIMIT_MOBILE : QUICK_FILTER_LIMIT_DESKTOP}
		/>
	) : null;

	const sortSelect = <SortLevels {...sort.levelsProps} hasCard={Boolean(uniqueCardInFiltered)} isMobile={isMobile} />;

	// The board's Columns and Swimlanes (KPR-1395), while it is on screen.
	const boardControls = boardOn && board ? (
		<BoardControls
			options={boardOptions}
			by={board.by.name}
			lanes={board.lanes?.name || null}
			onChange={changeBoard}
			isMobile={isMobile}
		/>
	) : null;

	// "Group · Day", beside the sort it follows — grouping only ever means
	// "cut the sorted feed where its date changes period". It goes quiet
	// (disabled, with the reason) wherever separators cannot apply.
	const groupDisabledReason = groupDisabledReasonFor({ viewMode, mapOn, boardOn, isDateSort });
	const groupOptions = useMemo(
		() => groupOptionsFor(allowSubDay).map(({ value, label }) => ({ value, label })),
		[allowSubDay]
	);

	// "Include linked": shown only where there is something to merge, and
	// saying how much: "Include linked (65)" — the linked records that match
	// this list's card filter, search and review filter (useLinkedCount),
	// i.e. what the switch adds or takes away. It sits with the filters, not
	// beside the list's own count: "Include linked  2 items" read as "include
	// the 2 linked items". The list's records from linked collections say
	// where they live, one by one (their qualified titles).
	const linkedLabel = linkedCount ? `Include linked (${linkedCount})` : 'Include linked';
	const linkedCountWords = linkedCount ? `${linkedCount} ${linkedCount === 1 ? 'record' : 'records'} from linked collections match this list` : null;
	const linkedToggle = linked.hasIncoming ? (
		<Tooltip title={[
			linkedOn
				? 'Showing records other collections share with this one, read-only'
				: 'Show records other collections share with this one',
			linkedCountWords,
		].filter(Boolean).join('. ')}
		>
			<label className="items-page__linked-toggle">
				<Switch
					size="small"
					checked={linkedOn}
					onChange={setLinkedOn}
					aria-label={linkedCount ? `Include linked collections' records (${linkedCount})` : 'Include linked collections\' records'}
					data-testid="include-linked"
				/>
				<span data-testid="include-linked-label">{linkedLabel}</span>
			</label>
		</Tooltip>
	) : null;

	// No count until the feed has answered for what is on screen — nor while
	// the first page has failed (components/items/ListCount).
	const countLabel = <ListCount count={totalCount} loading={itemsLoading} failed={firstPageFailed} />;
	// Refresh (KPR-1346): beside the count, a square glyph (standards §3).
	const refreshButton = (
		<RefreshButton
			pending={itemsReloading}
			onRefresh={handleRefresh}
			disabled={Boolean(worklistBlocked) || noCards}
		/>
	);

	// The intake quarantine, said on the filter row: the review filter in
	// force as a chip that clears it, or — on the ordinary list — the door
	// to the queue when the collection has submissions waiting
	// (`needsReview` on the collection record, the caller's own count).
	const needsReview = Number(collection?.needsReview) || 0;
	const reviewControl = review ? (
		<Tag
			closable
			className="items-page__review-chip"
			onClose={(event) => { event.preventDefault(); update({ review: undefined }); }}
			closeIcon={<CLOSE aria-label="Clear the review filter" />}
			data-testid="review-filter-chip"
		>
			{REVIEW_LABELS[review]}
		</Tag>
	) : needsReview > 0 ? (
		<Button
			size="small"
			className="items-page__review-door"
			onClick={() => update({ review: 'pending' })}
			data-action="show-review-queue"
		>
			{needsReview === 1 ? '1 needs review' : `${needsReview} need review`}
		</Button>
	) : null;

	// Arranging the tile is a property of ONE card, so it is offered only where
	// the list holds exactly one — the same gate the worklist uses — and only
	// in the grid, which is the view it changes. The tiers it can write are
	// the modal's business; it always offers at least "just me".
	const tileLayoutCard = (effectiveView === 'grid' || boardOn) && uniqueCardInFiltered
		? (cardsById?.[uniqueCardInFiltered] || null)
		: null;
	const tileLayoutButton = tileLayoutCard ? (
		<Tooltip title={`Arrange the ${tileLayoutCard.name || 'card'} tile`}>
			<Button
				className="items-page__tile-layout"
				icon={<GRID />}
				aria-label={`Arrange the ${tileLayoutCard.name || 'card'} tile`}
				onClick={() => setTileLayoutOpen(true)}
			/>
		</Tooltip>
	) : null;

	const collectionColumnButton = subCollections.length && viewMode === 'table' && !isMobile ? (
		<Button
			className="items-page__collection-column"
			icon={<COLUMNS />}
			aria-pressed={showCollectionColumn}
			type={showCollectionColumn ? 'default' : 'text'}
			onClick={toggleCollectionColumn}
		>
			{showCollectionColumn ? 'Hide sub-collection column' : 'Show sub-collection column'}
		</Button>
	) : null;

	const tableColumnsButton = tableColumnsCard ? (
		<Tooltip title={`Choose the ${tableColumnsCard.name || 'card'} columns`}>
			<Button
				className="items-page__table-columns"
				icon={<COLUMNS />}
				aria-label={`Choose the ${tableColumnsCard.name || 'card'} columns`}
				onClick={() => setTableColumnsOpen(true)}
			>
				{isMobile ? null : 'Columns…'}
			</Button>
		</Tooltip>
	) : null;

	// Grid and Table, plus Worklist where the one card in view declares one,
	// and Map where the list can be mapped (KPR-392).
	const viewSwitch = (
		<ViewModeSwitch
			value={effectiveView}
			onChange={changeViewMode}
			isMobile={isMobile}
			busy={worklistToolbar.busy}
			// The third segment appears only where it means something: a
			// single card that declares what "working through" its items is.
			extras={[
				...(worklistToolbar.segment ? [ worklistToolbar.segment ] : []),
				...(mapOffered ? [ {
					value: 'map',
					label: 'Map',
					title: 'Map',
					mobileIcon: <MAP role="img" aria-label="Map" />,
				} ] : []),
				// Board where the list's cards share an element to draw it by (KPR-1395).
				...(boardOffered ? [ {
					value: 'board',
					label: 'Board',
					title: 'Board',
					mobileIcon: <KANBAN role="img" aria-label="Board" />,
				} ] : []),
			]}
		/>
	);

	return (
		<FilterByValueProvider applyValueFilter={applyValueFilter} facetFor={facetFor}>
		<div className={`items-page${density === 'compact' ? ' items-page--compact' : ''}`}>
			<GlobalLiveNotice collection={collection} className="items-page__alert" />
			<GlobalDisclaimer collection={collection} className="items-page__alert" />
			{combinedScope.suggestion}
			{cardsError ? (
				<Alert
					type="warning"
					showIcon
					className="items-page__alert"
					message="Card definitions failed to load; item titles and fields may be incomplete."
				/>
			) : null}

			{/* The two toolbar rows (KPR-1633): what you are looking at, then how
			    it is narrowed and ordered; Display ▾ holds how the list is drawn.
			    CollectionItemsToolbar places the controls, on a phone as stacked rows. */}
			<CollectionItemsToolbar
				isMobile={isMobile}
				onClearFilter={() => setQuery(undefined)}
				c={{
					hasQuery: Boolean(query), searchBox, cardFilterSelect, subCollectionSelect, reviewControl, filterChips,
					worklistChips: worklistToolbar.filterChips, filterBuilder, quickFilters, savedFilters: savedFilterControls.menu,
					savedFilterCommit: savedFilterControls.commit, linkedToggle,
					scopeSwitch: combinedScope.scopeSwitch, sortSelect, boardControls, countLabel, refreshButton, viewSwitch,
				}}
				display={{
					viewMode: effectiveView, density, onDensityChange: setDensity,
					columnsButton: tableColumnsButton, collectionColumnButton, tileLayoutButton,
					group: { value: effectiveGroup, options: groupOptions, onChange: setGroupToken, disabledReason: groupDisabledReason, onCollapseAll: setAllCollapsed },
				}}
			/>

			{/* What the filter names that is gone, and what that did to the
			    list — from the server (X-Query-Warnings). Never blocks it. */}
			<QueryRefusalPick
				refusal={queryRefusal}
				onPick={pickForRefusal}
				onRemove={() => setQuery(undefined)}
				// Too long after a pick: forget this feed's picks, and the list
				// asks again.
				onRepick={repick}
			/>

			<FilterDriftWarning
				warnings={queryWarnings}
				omitted={queryWarningsOmitted}
				query={query}
				fields={filterFields}
				resolveCard={resolveFilterCard}
				onEditFilter={openFilterBuilder}
			/>

			<ItemsBulkSurfaces
				bulk={bulk}
				isMobile={isMobile}
				totalCount={totalCount}
				linkedOn={linkedOn}
				cardFilter={cardFilter}
				feedQuery={feedQuery}
				review={review}
				subCollections={subCollections}
				collection={collection}
				collectionId={collectionId}
				cardsById={cardsById}
				getElementsForCard={getElementsForCard}
				resolveElements={resolveElements}
				resolveLookupValue={resolveLookupValue}
				refreshItems={refreshItems}
			/>

			<div ref={paneWidth.bodyRef} className={`items-page__body${selectedItem || selectedGone ? ' items-page__body--with-pane' : ''}`}>
				<div className="items-page__list">
					{phoneNotice ? (
						<Alert
							type="warning"
							showIcon
							className="items-page__alert items-page__item-notice"
							message={phoneNotice === 'gone'
								? 'This item was deleted, or you can no longer see it.'
								: 'This item could not be opened.'}
							action={(
								<>
									{phoneNotice === 'failed' ? (
										<Button onClick={() => setPaneRefreshKey(key => key + 1)}>Try again</Button>
									) : null}
									<Button onClick={() => selectItem(null)}>Close</Button>
								</>
							)}
						/>
					) : null}
					{pinnedOpen ? (
						<div className="items-page__opened">
							<ItemCardsGrid
								{...listProps}
								items={[selectedItem]}
								loading={false}
								emptyText={null}
							/>
						</div>
					) : null}
					{worklistBlocked ? worklistBlockedBody : noCards ? (
						<NoCardsEmptyState collection={collection} collectionId={collectionId} />
					) : isEmpty && !isFiltered && !worklistFilter ? (
						// Cards but nothing in them and nothing narrowing the list:
						// the way to the first item (KPR-323). A filter, a card
						// type, a review pass or a worklist that matches nothing
						// is the branch below — "No items match", never this.
						<NoItemsEmptyState collection={collection} collectionId={collectionId} />
					) : isEmpty && search.applied ? (
						// A search that found nothing says what it looked for and
						// how to widen it (KPR-740) - in the triage view too: the
						// term narrows the queue the triager already sees.
						<NoItemsMatch
							term={search.applied}
							otherFilters={Boolean(search.otherFilters || cardFilter)}
							onClearSearch={search.clear}
							onClearFilters={() => update({ q: search.termOnlyQuery, card: undefined })}
						>
							{worklistFilter ? <Button onClick={() => changeViewMode('grid')}>Show all items</Button> : null}
						</NoItemsMatch>
					) : isEmpty ? (
						<div className="items-page__empty">
							{worklistFilter ? (
								// The narrowing came from the card, not from the
								// reader — clearing THEIR filters would not
								// widen it, so say whose it is and offer the
								// list without it.
								<>
									<span>No items match this card’s worklist filter</span>
									<Button onClick={() => changeViewMode('grid')}>Show all items</Button>
								</>
							) : isFiltered ? (
								<>
									<span>{review === 'pending' ? 'Nothing is waiting for review' : 'No items match'}</span>
									<Button onClick={() => update({ q: undefined, card: undefined, review: undefined })}>
										{review === 'pending' ? 'Show all items' : 'Clear filters'}
									</Button>
								</>
							) : null}
						</div>
					) : boardOn ? (
						// The board asks the list's own question for itself, as
						// the map does, and draws its own states (ItemsBoard).
						<ItemsBoard
							source={boardItems}
							by={board?.by || null}
							lanes={board?.lanes || null}
							cardsById={cardsById}
							getElementsForCard={getElementsForCard}
							tileLayouts={tileLayouts}
							resolvePrimaryDate={cardPrimaryDate}
							resolveLookupValue={resolveLookupValue}
							collection={collection}
							collectionId={collectionId}
							selectedItemId={selectedItemId || null}
							onSelectItem={selectItem}
							expandedItemId={isMobile ? selectedItemId : null}
							renderExpanded={isMobile ? renderExpanded : undefined}
							onItemUpdated={replaceItemEverywhere}
							onItemDeleted={handleItemDeleted}
							onItemRemoved={handleItemDeleted}
							isMobile={isMobile}
							rank={rankSort}
							onReload={boardItems.refresh}
						/>
					) : mapOn ? (
						// The map asks the list's question for itself, a
						// thousand at a time, and draws its own waiting,
						// failure and partial states (ItemsMapView).
						<ItemsMapView
							map={mapItems}
							placement={mapPlacement}
							getElementsForCard={getElementsForCard}
							cardsById={cardsById}
							resolveLookupValue={resolveLookupValue}
							collection={collection}
							selectedId={selectedItemId || null}
							onSelectItem={selectItem}
							expandedItemId={isMobile ? selectedItemId : null}
							renderExpanded={isMobile ? renderExpanded : undefined}
							isMobile={isMobile}
						/>
					) : firstPageFailed ? (
						// A first page that failed has no list to draw — not the
						// rows of the question before, and not an empty table
						// saying "No data" above the Retry row.
						null
					) : (
						// §14: the first load is a skeleton shaped like the rows
						// (after the delay, held its minimum), and a page, sort or
						// filter change keeps the rows on screen under the quiet
						// refreshing bar. An empty list is never drawn from here:
						// the branches above need `isLoading` to be false.
						// A Refresh is the refreshing state too (the rows stay, a
						// quiet bar), and one that fails sits above the rows
						// with Retry (KPR-1346).
						<ItemsListBody
							view={viewMode === 'table' ? 'table' : 'grid'}
							items={sortedItems}
							loading={isLoading || itemsReloading}
							error={itemsReloadError ? (itemsReloadError.kind === 'rate_limited' ? RATE_LIMITED_TEXT : 'Showing what was loaded before.') : null}
							onRetry={handleRefresh}
							label="items"
						>
							{listBody}
						</ItemsListBody>
					)}

					{/* A blocked worklist pages nothing: `hasMore` speaks for the
					    unfiltered feed whose rows are deliberately not on screen. */}
					<ListScrollLoader
						hasMore={hasMore}
						loadMore={loadMore}
						loadingMore={loadingMore}
						landedPages={landedPages}
						error={itemsLoadError}
						onRetry={retryItems}
						nothingToFill={nothingToFill}
						// The map reads its items itself; the feed's scroll
						// paging stands down under it.
						suspended={Boolean(worklistBlocked) || mapOn || boardOn}
						rearmKey={viewMode}
					/>
				</div>

				{/* Mobile primary: a FAB above the tab bar (the context bar's
				    Add item button is hidden behind wrap there). While a
				    selection is on, the selection bar owns that corner —
				    both cannot sit there, and a toolbar never scrolls
				    sideways to fit two (§5 Lists). */}
				{(selectionEnabled && selection.active) || !canCreateItems ? null : (
					<Button
						type="primary"
						className="items-page__fab"
						icon={<PLUS />}
						onClick={() => document.querySelector('[data-action="add-item"]')?.click()}
					>
						Add item
					</Button>
				)}

				{/* The pane: desktop always. A phone opens the item in its own
				    tile (or at the top of the list when no tile of the feed's
				    holds it), so the pane is only the board's and the map's and
				    a worklist's fallback there (KPR-1244). */}
				{selectedGone && !isMobile ? (
					<ItemPaneGone onClose={() => selectItem(null)} />
				) : null}
				{selectedItem && (!isMobile || (!selectedShownInFeed && !phoneOpensInline)) ? (
					<ItemDetailPane
						item={selectedItem}
						card={selectedCard}
						elementDefs={resolveElements(selectedItem?.card_id)}
						resolveLookupValue={resolveLookupValue}
						collectionId={itemHomeCollectionId(selectedItem, collectionId)}
						collection={isReadOnlyRow(selectedItem, collection) ? null : collection}
						onClose={() => selectItem(null)}
						canEdit={canEditItem(selectedItem)}
						onDeleted={handleItemDeleted}
						onIntakeReviewed={handleIntakeReviewed}
						canManage={isReadOnlyRow(selectedItem, collection) ? false : canManage}
						readOnlyRow={isReadOnlyRow(selectedItem, collection)}
						cardsById={cardsById}
						onItemUpdated={replaceItemEverywhere}
						reloadKey={paneRefreshKey}
						width={paneWidth.width}
						wideAllowed={paneWidth.wideAllowed}
						onWidth={paneWidth.setWidth}
						onStep={stepSelection}
						editInPlace={!isMobile}
						controlRef={paneRef}
					/>
				) : null}
			</div>

			{/* Arranging the tile of the one card in view. The preview reads a
			    REAL record from the feed, because an arrangement is judged on
			    actual values, not on samples. */}
			{tileLayoutCard ? (
				<TileLayoutModal
					open={tileLayoutOpen}
					onClose={() => setTileLayoutOpen(false)}
					card={tileLayoutCard}
					elementDefs={getElementsForCard(tileLayoutCard._id)}
					collection={collection}
					collectionId={collectionId}
					sampleItem={sortedItems.find(item => normalizeId(item.card_id) === uniqueCardInFiltered) || null}
					itemsById={itemsById}
					onSaved={refreshTileLayouts}
				/>
			) : null}
			{tableColumnsCard ? (
				<TableColumnsModal
					open={tableColumnsOpen}
					onClose={() => setTableColumnsOpen(false)}
					card={tableColumnsCard}
					elementDefs={getElementsForCard(tableColumnsCard._id)}
					collection={collection}
					collectionId={collectionId}
					currentColumns={tableColumnsNow}
					// The person's own arrangement: a filter's columns stop standing in.
					onSaved={() => { setSavedColumns([]); refreshTableLayouts(); }}
				/>
			) : null}
		</div>
		</FilterByValueProvider>
	);
}
